import { definitions } from '@luxuryescapes/contract-svc-promo'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { renderMarkdown } from 'lib/string/markdownUtils'

function mapCompetitionGrandPrize(grandPrize: definitions['CompetitionConfigV2']['grandPrizeDetails']): App.MarketingGrandPrizeDetail {
  return {
    offerId: grandPrize.offerId,
    title: grandPrize.title,
    link: grandPrize.link,
    includes: grandPrize.includes.map(include => include) ?? [],
    offer: {
      description: grandPrize.offer.description,
      name: grandPrize.offer.name,
      subtitle: grandPrize.offer.subtitle,
      image: {
        id: grandPrize.offer.imageId,
      },
    },
  }
}

function mapCompetitionHeadData(headData: any) {
  return {
    thumbnailImage: { id: headData.thumbnailImageId },
  }
}

function mapCompetitionPayoutConfig(payout: definitions['PayoutConfigV2']): definitions['PayoutConfigV2'] {
  return {
    id: payout.id,
    weightedChance: payout.weightedChance,
    value: payout.value,
  }
}

function mapMinorPrizeDetails(prizeDetail: any): App.MarketingCompetitionMinorPrizeDetails {
  return {
    codeName: prizeDetail.codeName,
    promoId: prizeDetail.promoIde,
    promoType: prizeDetail.promoType,
    expiryDate: prizeDetail.expiryDate,
    discountValue: prizeDetail.discountValue,
    minSpend: prizeDetail.minSpend,
    maxDiscount: prizeDetail.maxDisscount,
  }
}

export function mapCompetitionConfig(data: definitions['CompetitionConfigV2']): App.MarketingCompetitionConfig {
  return {
    allowedRegions: data.allowedRegions.map(region => region) ?? [],
    episodeId: data.episodeId ?? undefined,
    prizeType: data.prizeType,
    drawFrequency: data.drawFrequency,
    startDateUtc: data.startDateUtc,
    endDateUtc: data.endDateUtc,
    drawDateUtc: data.drawDateUtc,
    heroImage: { id: data.heroImageId },
    heroTextIntro: data.heroTextIntro,
    heroTextHighlight: data.heroTextHighlight,
    promoLink: data.promo_link,
    faqSlug: data.faqSlug,
    payoutConfig: data.payoutConfig.map(mapCompetitionPayoutConfig) ?? [],
    images: data.images.map(image => ({
      id: image.imageId,
      title: image.imageTitle,
    })),
    grandPrizeDetails: mapCompetitionGrandPrize(data.grandPrizeDetails),
    prizeContactDateUtc: data.prizeContactDateUtc,
    header: data.header,
    title: data.title,
    bodyText: data.bodyText,
    footerText: data.footerText,
    breadcrumb: data.breadcrumb,
    headData: mapCompetitionHeadData(data.headData),
  }
}

export function mapCompetitionMinorPrize(data: any): App.MarketingCompetitionMinorPrize {
  return {
    prizeType: data.prizeType,
    drawFrequency: data.drawFrequency,
    prizeValue: data.prizeValue,
    prizeDetails: data.prizeDetails ? mapMinorPrizeDetails(data.prizeDetails) : undefined,
  }
}

export function mapCompetitionUserStatus(data: any): App.MarketingCompetitionUserStatus {
  if (Object.keys(data.prizeDetails).length === 0 || !data.alreadyHasReward) {
    return {
      alreadyHasReward: data.alreadyHasReward,
      prizeDetails: null,
    }
  }
  if (data.prizeDetails.competitionType === 'promo') {
    return {
      alreadyHasReward: data.alreadyHasReward,
      ...mapCompetitionStatusPromoPrize(data.prizeDetails),
    }
  }
  return {
    alreadyHasReward: data.alreadyHasReward,
    competitionType: 'raffle',
    prizeValue: data.prizeDetails.prizeValue,
  }
}

export function mapContentfulWebPageData(data: any): App.ContentfulWebPageData {
  return {
    title: data.title,
    destination: data.slug,
    seoTitle: data.seoTitle,
    seoDescription: data.seoDescription,
    components: data.components.map(mapContentfulWebPageComponent),
    modalComponents: data.modalComponents?.map(mapContentfulWebPageModalComponent),
  }
}

export function mapContentfulNavBar(data: any): Array<App.ContentfulNavBarItem> {
  return data.navBar ? data.navBar.map((item: any) => ({
    title: item.fields.title,
    subtitle: item.fields.subtitle,
    to: item.fields.linkTo,
  })) : []
}

export function mapContentfulWebPageComponent(data: any): App.ContentfulWebPageComponent {
  switch (data.contentTypeId) {
    case 'componentHeroImage':
      return {
        title: data.title,
        subtitle: data.subtitle,
        image: { id: data.imageId },
        contentTypeId: data.contentTypeId,
        toggle: data.toggle,
        navBar: mapContentfulNavBar(data),
        type: data.types,
      }
    case 'componentHeroCarousel':
      return {
        title: data.title,
        heroImageTitle: data.heroImageTitle,
        subtitle: data.heroImageSubtitle,
        image: { id: data.heroImageId },
        contentTypeId: data.contentTypeId,
        filters: {
          offerTypes: data.offerTypes ? data.offerTypes.map((offerType: string) => offerType) : [],
          placeIds: data.placeIDs && data.placeIDs.length > 0 ? data.placeIDs.map((placeId: string) => placeId) : [ANYWHERE_PLACE_ID],
          campaigns: data.campaigns ? data.campaigns.map((campaign: string) => campaign) : [],
          limit: data.limit,
        },
      }
    case 'componentCardCarousel':
      return {
        title: data.title,
        subtitle: data.subtitle,
        cardItems: data.cardItems.map(mapContentfulWebPageCardItem),
        viewAllLink: data.viewAllLink,
        isLargeContentWrapper: data.isLargeContentWrapper,
        contentTypeId: data.contentTypeId,
      }
    case 'componentFlightDealsCarousal':
      return {
        title: data.title,
        category: data.subtitle,
        originAirportCodes: data.originAirportCodes ? data.originAirportCodes.map((originAirportCode: string) => originAirportCode) : [],
        destinationAirportCode: data.destinationAirportCode,
        viewAllLink: data.viewAllLink,
        contentTypeId: data.contentTypeId,
      }
    case 'componentOfferListCarousel':
      return {
        title: data.title,
        filters: {
          offerTypes: data.offerTypes ? data.offerTypes.map((offerType: string) => offerType) : [],
          campaigns: data.campaigns ? data.campaigns.map((campaign: string) => campaign) : [],
          placeIds: data.placeIds && data.placeIds.length > 0 ? data.placeIds.map((placeId: string) => placeId) : [ANYWHERE_PLACE_ID],
          limit: data.offersToShow,
        },
        viewAllLink: data.viewAllLink,
        contentTypeId: data.contentTypeId,
        lookingForSomethingElseOption: data.lookingForSomethingElseLink,
        showFilters: data.showFilters ?? true,
      }
    case 'componentSupport':
      return {
        contentTypeId: data.contentTypeId,
        title: data.title,
      }
    case 'componentValuePropositionBanner':
      return {
        contentTypeId: data.contentTypeId,
        title: data.title,
        items: data.items.map(mapComponentValuePropositionItems),
      }
    case 'componentExperienceOfferListCarousel':
      return {
        title: data.title,
        destination: data.destination,
        contentTypeId: data.contentTypeId,
        campaigns: data.campaigns,
      }
    case 'componentTileNavigation':
      return {
        title: data.title,
        tiles: data.tiles.map((tile: any) => tile.fields),
        maxTilesVisible: data.maxTilesVisible,
        variant: data.variant,
        contentTypeId: data.contentTypeId,
      }
    case 'componentWings':
      return {
        title: data.slug,
        banners: data.placements.map((placement: any) => ({
          name: placement.fields.name,
          bannerId: placement.fields.bannerId,
          imageId: placement.fields.desktopImageId,
          imageAlt: placement.fields.desktopImageId,
          position: 'left',
          href: placement.fields.linkTo,
          desktopImageId: placement.fields.desktopImageId,
          mobileImageId: placement.fields.mobileImageId,
        })),
        contentTypeId: data.contentTypeId,
      }
    case 'componentVideoBlock':
      return mapContentfulVideoBlock(data)
    case 'componentReviews':
      return {
        contentTypeId: data.contentTypeId,
        title: data.title,
      }
    case 'componentImageTileCarousel':
      return {
        contentTypeId: data.contentTypeId,
        title: data.title,
        showTitle: data.showTitle || data.showTitle === undefined,
        tiles: data.tiles.map(mapContentfulWebPageImageTile),
      }
    case 'componentVerticalOfferList':
      return {
        contentTypeId: data.contentTypeId,
        title: data.title,
        showFilters: data.showFilters,
        showSearchHeader: data.showSearchHeader || data.showSearchHeader === undefined,
        campaigns: data.campaigns ? data.campaigns : [],
        offerTypes: data.offerTypes ? data.offerTypes : [],
        placeIds: data.placeIds ? data.placeIds : [],
        isInfiniteScroll: data.isInfiniteScroll,
      }
    case 'legalPages':
      return {
        contentTypeId: 'componentFaqs',
        content: data.pageContent.map((content: any) => ({
          answer: content.fields.answer,
          question: content.fields.title,
        })),
        title: data.title,
      }
    case 'componentIframe':
      return {
        contentTypeId: 'componentIframe',
        title: data.title,
        url: data.url,
        frameHeight: data.frameHeight,
        mobileFrameHeight: data.mobileFrameHeight,
      }
    case 'componentLogoCarousel':
      return {
        contentTypeId: 'componentLogoCarousel',
        images: data.images.map((image: any) => ({
          title: image.fields.title,
          id: image.fields.id,
        })),
        title: data.title,
        backgroundColor: data.backgroundColor || 'white',
        withOutline: data.withOutline,
      }
    case 'componentForm':
      return mapComponentForm(data)
    case 'componentVerticalOfferListsGroup': {
      return {
        contentTypeId: 'componentVerticalOfferListsGroup',
        title: 'title',
        offerListsGroup: data.offerListsGroup.map((payload: any) => ({
          location: payload.fields.location,
          offerType: payload.fields.offerType,
        })),
      }
    }
    case 'componentSpacer': {
      return {
        title: data.title,
        contentTypeId: 'componentSpacer',
        space: data.space,
        backgroundColor: data.backgroundColor,
      }
    }
    case 'componentCampaignHero': {
      return {
        contentTypeId: 'componentCampaignHero',
        title: data.title,
        backGroundImage: { id: data.backgroundImageId },
        linkTo: data.linkTo,
        bodyText: data.bodyText,
        footerText: data.footerText,
        promoCode: data.promoCode,
        logoImageId: { id: data.logoImageId },
        labels: data.labels ? data.labels.map((label: string) => label) : [],
      }
    }
    case 'bannerGroup':
      return {
        contentTypeId: 'componentBannerGroup',
        title: data.title,
        subtitle: data.subtitle,
        banners: data.banners.map(mapDynamicBanner),
        slug: data.slug,
      }
    case 'componentOfferGridView':
      return {
        contentTypeId: 'componentOfferGridView',
        title: data.title,
        placeIds: data.placeIds ? data.placeIds.map((placeId: string) => placeId) : [ANYWHERE_PLACE_ID],
        offerTypes: data.offerTypes ? data.offerTypes.map((offerType: string) => offerType) : [],
        campaigns: data.campaigns ? data.campaigns.map((campaign: string) => campaign) : [],
        offersToShow: data.offersToShow,
      }
    default:
      return {
        title: 'Component not found',
        contentTypeId: 'componentNotFound',
      }
  }
}

export function mapContentfulVideoBlock(data: any): App.ComponentVideoBlock {
  return {
    caption: data.caption,
    title: data.title,
    subtitle: data.subtitle,
    bodyText: data.bodyText,
    contentAlign: data.contentAlign || 'start',
    contentPosition: data.contentPosition,
    videoId: data.videoId,
    contentTypeId: data.contentTypeId,
    image: { id: data.imageId },
    backgroundColor: data.background,
    buttonBlocks: data.buttonBlocks ? data.buttonBlocks.map((buttonBlock: any) => ({
      title: buttonBlock.fields.title,
      linkTo: buttonBlock.fields.linkTo,
      kind: buttonBlock.fields.kind,
      size: buttonBlock.fields.size,
      fit: buttonBlock.fields.fit,
      productTheme: buttonBlock.fields.productTheme === 'loyalty' ? 'lux-plus' : buttonBlock.fields.productTheme,
    })) : [],
    buttonDirection: data.buttonDirection || 'horizontal',
  }
}

export function mapContentfulWebPageModalComponent(data: any): App.ContentfulWebPageModalComponent {
  switch (data.contentTypeId) {
    case 'componentForm':
      return mapComponentForm(data)
    case 'componentContentModal': {
      return {
        contentTypeId: 'componentContentModal',
        title: data.title,
        image: { id: data.imageId },
        bodyText: data.bodyText,
        slug: data.slug,
      }
    }
    default:
      return {
        title: 'Component not found',
        contentTypeId: 'componentNotFound',
        slug: 'unknown-component',
      }
  }
}

function mapContentfulWebPageImageTile(data: any): App.ComponentImageTileType {
  if (data.sys.contentType.sys.id === 'componentImageTextTile') {
    return {
      contentTypeId: 'componentImageTextTile',
      tag: data.fields.tag,
      title: data.fields.title,
      subtitle: data.fields.subtitle,
      linkTo: data.fields.linkTo,
      linkText: data.fields.linkText,
      image: { id: data.fields.imageId },
    }
  }
  return {
    contentTypeId: 'componentImageTile',
    image: { id: data.fields.imageId },
    title: data.fields.title,
    subtitle: data.fields.subtitle,
    linkTo: data.fields.linkTo,
    linkText: data.fields.linkText,
  }
}

function mapDynamicBanner(data: any): App.ComponentDynamicBanner {
  return {
    title: data.fields.title,
    image: { id: data.fields.imageId },
    linkTo: data.fields.linkTo,
    backgroundColor: data.fields.backgroundColor,
    footerText: data.fields.footerText,
    footerSubText: data.fields.footerSubText,
    labels: data.fields.labels ? data.fields.labels.map((label: string) => label) : undefined,
  }
}

function mapComponentForm(data: any): App.ComponentForm {
  return {
    contentTypeId: 'componentForm',
    title: data.title,
    subtitle: data.subtitle,
    buttonText: data.buttonText,
    successMessage: data.successMessage,
    backgroundColor: data.backgroundColor,
    slug: data.slug,
    components: data.components.map((component: any) => ({
      type: component.fields.type,
      label: component.fields.label,
      placeholder: component.fields.placeholder,
      isRequired: component.fields.isRequired,
      options: component.fields.options,
    })),
    loginRequired: !!data.loginRequired,
    loginRequiredButtonText: data.loginRequiredButtonText,
    postSubmissionState: data.postSubmissionState?.fields ? mapContentfulWebPageComponent({ ...data.postSubmissionState.fields, contentTypeId: 'componentVideoBlock', title: renderMarkdown(data.postSubmissionState.fields.title) }) as App.ComponentVideoBlock : undefined,
  }
}

export function mapContentfulWebPageCardItem(data: any): App.ContentfulWebPageCardItem {
  switch (data.sys.contentType.sys.id) {
    case 'componentPopularRegionTile':
      return {
        title: data.fields.title,
        imageId: data.fields.imageId,
        description: data.fields.description,
        url: data.fields.linkTo,
        type: data.sys.contentType.sys.id,
      }
    case 'componentWhyWeLoveTile':
      return {
        title: data.fields.title,
        imageId: data.fields.imageId,
        description: data.fields.description,
        type: data.sys.contentType.sys.id,
      }
    case 'componentBlogCard':
      return {
        type: data.sys.contentType.sys.id,
        title: data.fields.title,
        imageId: data.fields.imageId,
        description: data.fields.description,
        slug: data.fields.slug,
        date: data.fields.date,
      }
  }
  return {
    title: data.fields.title,
    imageId: data.fields.imageId,
    description: data.fields.description,
    url: data.fields.linkTo,
    type: data.sys.contentType.sys.id,
    slug: data.fields.slug,
    date: data.fields.date,
  }
}

function mapCompetitionStatusPromoPrize(data: any): Omit<App.MarketingCompetitionUserStatusPromo, 'alreadyHasReward'> {
  return {
    competitionType: 'promo',
    codeName: data.codeName,
    promoId: data.promoId,
    promoType: data.promoType,
    expiryDate: data.expiryDate,
    discountValue: data.discountValue,
    minSpend: data.minSpend,
    maxDiscount: data.maxDiscount,
  }
}

function mapComponentValuePropositionItems(item: any): App.ComponentValuePropositionBlock {
  return {
    title: item.fields.title,
    subtitle: item.fields.subtitle,
    icon: item.fields.icon,
    linkText: item.fields.linkText,
    linkTo: item.fields.linkTo,
  }
}
